'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useStytchMember } from '@stytch/nextjs/b2b'
import {
  SELECTED_CLIENT_ID_CHANGED_EVENT,
  getSelectedClientId,
  removeSelectedClientId,
  setSelectedClientId as persistSelectedClientId,
} from '@/utilities/client/selectedClientIdStorage'
import { syncPostHogPartnerGroup } from '@/utilities/analytics/partnerGroupAnalytics'
import { PARTNERS_DATA_UPDATED_EVENT } from '@/utilities/partnersCache'

type ClientSelectionContextValue = {
  selectedClientId: string | null
  isSelectionReady: boolean
  setSelectedClientId: (clientId: string | null) => void
}

const ClientSelectionContext = createContext<ClientSelectionContextValue | undefined>(undefined)

const readSelectedClientIdFromStorage = (): string | null => {
  try {
    return getSelectedClientId()
  } catch {
    return null
  }
}

export function ClientSelectionProvider({ children }: { children: React.ReactNode }) {
  const { member, isInitialized } = useStytchMember()
  const hasActiveSession = isInitialized && Boolean(member)

  // Null on first render so SSR and client hydration match. Storage is read in useLayoutEffect
  // before paint, so PostHog group sync runs earlier than useEffect.
  // isSelectionReady stays false until that read. A null id is unresolved only before the read.
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)
  const [isSelectionReady, setIsSelectionReady] = useState(false)
  const selectedClientIdRef = useRef(selectedClientId)
  useEffect(() => {
    selectedClientIdRef.current = selectedClientId
  }, [selectedClientId])

  const syncAnalyticsForClientId = useCallback((clientId: string) => {
    syncPostHogPartnerGroup(clientId)
  }, [])

  const syncAnalyticsIfAuthenticated = useCallback(
    (clientId: string) => {
      if (!hasActiveSession) return
      syncAnalyticsForClientId(clientId)
    },
    [hasActiveSession, syncAnalyticsForClientId],
  )

  useLayoutEffect(() => {
    const saved = readSelectedClientIdFromStorage()
    // eslint-disable-next-line react-hooks/set-state-in-effect -- state update required inside this async/side-effect; cannot be derived
    setSelectedClientId(saved)
    setIsSelectionReady(true)
    if (saved) {
      syncAnalyticsIfAuthenticated(saved)
    }
  }, [syncAnalyticsIfAuthenticated])

  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail
      if (typeof detail === 'string') {
        setSelectedClientId(detail)
        syncAnalyticsIfAuthenticated(detail)
      }
    }
    window.addEventListener(SELECTED_CLIENT_ID_CHANGED_EVENT, listener)
    return () => window.removeEventListener(SELECTED_CLIENT_ID_CHANGED_EVENT, listener)
  }, [syncAnalyticsIfAuthenticated])

  useEffect(() => {
    const retrySync = () => {
      const clientId = selectedClientIdRef.current
      if (clientId) {
        syncAnalyticsIfAuthenticated(clientId)
      }
    }
    window.addEventListener(PARTNERS_DATA_UPDATED_EVENT, retrySync)
    return () => window.removeEventListener(PARTNERS_DATA_UPDATED_EVENT, retrySync)
  }, [syncAnalyticsIfAuthenticated])

  const updateSelection = useCallback(
    (clientId: string | null) => {
      if (clientId === selectedClientIdRef.current) return
      setSelectedClientId(clientId)
      try {
        if (clientId) {
          persistSelectedClientId(clientId)
          syncAnalyticsIfAuthenticated(clientId)
        } else {
          removeSelectedClientId()
        }
      } catch {
        /** noop */
      }
    },
    [syncAnalyticsIfAuthenticated],
  )

  const value = useMemo<ClientSelectionContextValue>(
    () => ({
      selectedClientId,
      isSelectionReady,
      setSelectedClientId: updateSelection,
    }),
    [isSelectionReady, selectedClientId, updateSelection],
  )

  return <ClientSelectionContext.Provider value={value}>{children}</ClientSelectionContext.Provider>
}

export function useClientSelection(): ClientSelectionContextValue {
  const context = useContext(ClientSelectionContext)
  if (!context) {
    throw new Error('useClientSelection must be used within a ClientSelectionProvider')
  }
  return context
}
